import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Plus, Trash2 } from 'lucide-react';
import { TILE_PLACEMENTS } from '../../config/executiveTileCatalog';
import { customExecutiveMetricsService } from '../../services/customExecutiveMetricsService';

const UNIT_OPTIONS = [
  { value: 'count', label: 'Count' },
  { value: 'percent', label: 'Percent' },
  { value: 'currency', label: 'Currency ($)' },
  { value: 'days', label: 'Days' },
];

const PLACEMENT_OPTIONS = [
  { value: TILE_PLACEMENTS.TOP_KPI, label: 'Executive — top KPI row' },
  { value: TILE_PLACEMENTS.SCORECARD_METRIC, label: 'Executive — department scorecard metric' },
  { value: TILE_PLACEMENTS.OPERATIONAL_HIGHLIGHT, label: 'Operational — highlight row' },
];

const SCORECARD_OPTIONS = [
  { value: 'ceo', label: 'CEO / Growth' },
  { value: 'cfo', label: 'CFO / Finance' },
  { value: 'coo', label: 'COO / Operations' },
  { value: 'cmo', label: 'CMO / Medical' },
  { value: 'dental', label: 'Dental Services' },
  { value: 'hr', label: 'HR / Workforce' },
  { value: 'pharmacy', label: 'Pharmacy' },
  { value: 'behavioral_health', label: 'Behavioral Health' },
  { value: 'revenue_cycle', label: 'Revenue Cycle (CRO)' },
];

const EMPTY_FORM = {
  label: '',
  value: '',
  compareValue: '',
  unit: 'count',
  placement: TILE_PLACEMENTS.TOP_KPI,
  scorecardId: 'coo',
  metricLabel: '',
  sourceLabel: 'Manual entry',
};

export default function ExecutiveCustomTilesSection({ organizationId, disabled }) {
  const [tiles, setTiles] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadTiles = useCallback(async () => {
    const data = await customExecutiveMetricsService.getCustomMetrics(organizationId, {
      includeHistory: false,
    });
    setTiles(data.tiles || []);
  }, [organizationId]);

  useEffect(() => {
    loadTiles();
  }, [loadTiles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return;
    setSaving(true);
    setMessage('');
    try {
      await customExecutiveMetricsService.saveCustomTile(organizationId, form);
      setForm(EMPTY_FORM);
      await loadTiles();
      setMessage(
        form.placement === TILE_PLACEMENTS.OPERATIONAL_HIGHLIGHT
          ? 'Custom metric saved. It will appear on the Operational Dashboard.'
          : 'Custom metric saved. It will appear on the Executive KPI Dashboard.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (tileId) => {
    await customExecutiveMetricsService.removeCustomTile(organizationId, tileId);
    await loadTiles();
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <LineChart className="w-5 h-5 text-blue-600" />
          Practice analytics tiles
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Add operational, clinical, or finance metrics from outside RCM — EHR exports, GL feeds, or manual
          leadership numbers. Tiles can appear on the Executive KPI Dashboard or the Operational Dashboard
          highlight row.
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Prefer automated feeds? Connect sources on{' '}
          <Link to="/integrations" className="text-blue-600 hover:underline">
            Integrations
          </Link>{' '}
          or upload scheduled reports via Practice Data.
        </p>
      </div>

      {disabled && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Select an organization to manage executive tiles.
        </p>
      )}

      <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <p className="text-sm font-medium text-gray-800">Add metric</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="e.g. No-show rate"
              disabled={disabled || saving}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Data source label</label>
            <input
              type="text"
              value={form.sourceLabel}
              onChange={(e) => setForm((f) => ({ ...f, sourceLabel: e.target.value }))}
              placeholder="e.g. EHR monthly export"
              disabled={disabled || saving}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Current value</label>
            <input
              type="number"
              step="any"
              value={form.value}
              onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
              disabled={disabled || saving}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Compare value (optional)</label>
            <input
              type="number"
              step="any"
              value={form.compareValue}
              onChange={(e) => setForm((f) => ({ ...f, compareValue: e.target.value }))}
              disabled={disabled || saving}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
            <select
              value={form.unit}
              onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
              disabled={disabled || saving}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              {UNIT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Placement</label>
            <select
              value={form.placement}
              onChange={(e) => setForm((f) => ({ ...f, placement: e.target.value }))}
              disabled={disabled || saving}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              {PLACEMENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {form.placement === TILE_PLACEMENTS.SCORECARD_METRIC && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Department scorecard</label>
                <select
                  value={form.scorecardId}
                  onChange={(e) => setForm((f) => ({ ...f, scorecardId: e.target.value }))}
                  disabled={disabled || saving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  {SCORECARD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Metric row label (optional)</label>
                <input
                  type="text"
                  value={form.metricLabel}
                  onChange={(e) => setForm((f) => ({ ...f, metricLabel: e.target.value }))}
                  placeholder="Defaults to tile label"
                  disabled={disabled || saving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </>
          )}
        </div>
        <button
          type="submit"
          disabled={disabled || saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          Add to dashboard
        </button>
        {message && <p className="text-sm text-green-700">{message}</p>}
      </form>

      {tiles.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-2">Label</th>
                <th className="px-4 py-2">Value</th>
                <th className="px-4 py-2">Placement</th>
                <th className="px-4 py-2">Source</th>
                <th className="px-4 py-2 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tiles.map((tile) => (
                <tr key={tile.id}>
                  <td className="px-4 py-2 font-medium text-gray-900">{tile.label}</td>
                  <td className="px-4 py-2 text-gray-700">
                    {tile.value != null ? `${tile.value}${tile.unit === 'percent' ? '%' : ''}` : '—'}
                  </td>
                  <td className="px-4 py-2 text-gray-600">
                    {tile.placement === TILE_PLACEMENTS.SCORECARD_METRIC
                      ? `Executive scorecard: ${tile.scorecardId}`
                      : tile.placement === TILE_PLACEMENTS.OPERATIONAL_HIGHLIGHT
                        ? 'Operational highlight'
                        : 'Executive top KPI'}
                  </td>
                  <td className="px-4 py-2 text-gray-500">{tile.sourceLabel}</td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => handleRemove(tile.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
